import React from "react";
import sortyBy from  "lodash/sortBy";

import { IResizeEntry, ResizeSensor } from "@blueprintjs/core";
import Logger from "core/util/Logger";
import { ILayout, LayoutTypeRegistry } from "./ILayoutType";
import { BoxVariant } from "common/views/box/Box.view";

/**
 * layout renderer component
 */

interface ILayoutProps<T> {
	data: ILayout<T>;
	renderRegion: (data: T, index: number, regionUuid?: string ) => React.ReactNode;
}

interface ILayoutState {
	current: string | null; // currently evaluated layout key
}

export default class Layout<T> extends React.PureComponent<ILayoutProps<T>, ILayoutState> {

	private container = React.createRef<HTMLDivElement>();

	public state: ILayoutState = { current: null };

	public render() {
		const { data } = this.props;

		// 1. no layouts configured.
		if (!data || !data.layouts || Object.keys(data.layouts).length < 1) {
			return null;
		}

		const { current } = this.state;

		const sortedKey = data.overrides && Object.entries(data.overrides)
			.sort(([ka, av], [kb, bv]) => av.breakpoint - bv.breakpoint)
			.map(([k]) => k)
			.shift();
		
		const layoutKey = current || sortedKey || data.layoutKey;

		// 2. find selected/current layout definition
		const sLayout = current && data.layouts[current] || data.layoutKey && data.layouts[data.layoutKey] || null;

		if (!sLayout) {
			Logger.error("Invalid layout key");
			return null;
		}

		// 3. find registered layout type for selected
		const layoutType = LayoutTypeRegistry.getType(sLayout.type);

		if (!layoutType) {
			return null;
		}

		// 3. if there could be more than one layout option, render with resize sensor.
		if (data.overrides && data.overrides.length > 0 && Object.keys(data.layouts).length > 1) {

			return (
				<ResizeSensor onResize={this.evaluateLayout}>
					<div ref={this.container}>
						{layoutType.renderLayout(sLayout.config, this.renderRegion, layoutKey)}
					</div>
				</ResizeSensor>
			);
		}

		// 4. if only one, just render the layout
		return layoutType.renderLayout(sLayout.config, this.renderRegion, layoutKey);
	}

	public componentDidMount() {
		// evaluate initial size
		this.evaluateLayout();
	}

	private renderRegion = (uuid: string, index: number) => {
		const { data: { regions = {} } = {}, renderRegion } = this.props;
		if (!regions[uuid]) {
			return null;
		}
		return renderRegion(regions[uuid].data || {} as T, index, uuid);
	}

	private evaluateLayout = () => {
		const el = this.container.current;
		if (!el) {
			return;
		}

		// Logger.debug(`width: [${el.getBoundingClientRect().width}] height: [${el.getBoundingClientRect().height}]`);

		const elementWidth = el.getBoundingClientRect().width;

		// 1. if there are any layouts specified, evaluate them against current wrapper size
		const { data } = this.props;

		let targetLayoutKey = data.layoutKey;

		for (const o of sortyBy(data.overrides, "breakpoint", "desc")) {
			if (o.layoutKey && o.breakpoint && o.breakpoint > elementWidth) {
				targetLayoutKey = o.layoutKey;
				break;
			}
		}

		// 2. if the computed layout key is different than the current one in state, set state to update

		if (
			this.state.current === null ? (targetLayoutKey !== data.layoutKey) : (targetLayoutKey !== this.state.current)
		) {
			this.setState({current: targetLayoutKey});
		}
	}
}



// WEBPACK FOOTER //
// ./src/common/form/fields/layout/Layout.tsx